import { useCallback } from 'react'
import { IMyFilesMediaItem } from 'types/media'
import { dispatch } from 'store'
import store from 'store'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import {
  convertImgFilesToMediaItem,
  convertMediaFilesToReferenceImages
} from './utils'
import { calculateTotalFilesSize } from 'utils/media'
import { useSelector } from 'react-redux'
import { useMyFilesFormatValidator } from 'hooks/useFileFormatValidator'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { checkStorageBeforeUpload } from 'store/features/user/utils'

interface IUseReferenceImageUploadReturn {
  isFilesUploading: boolean
  handleFilesUpload: (files: File[] | FileList) => Promise<void>
  handleMediaLibFiles: (files: IMyFilesMediaItem[]) => Promise<void>
}

export const useReferenceImageUpload = (
  showWarning: (value: boolean) => void
): IUseReferenceImageUploadReturn => {
  const isRefImagesUploading = useSelector(
    aiImageSelectors.isRefImagesUploading
  )
  const availableRefImgsCount = useSelector(
    aiImageSelectors.availableRefImgsCount
  )
  const { setAlertPopupsData } = useAlertPopupsContext()

  const setUploadingState = useCallback(() => {
    dispatch(aiImageThunks.setIsRefImagesUploading(true))
  }, [])

  const resetUploadingState = useCallback(() => {
    dispatch(aiImageThunks.setIsRefImagesUploading(false))
  }, [])

  const handleFilesUpload = useCallback(
    async (files: File[] | FileList) => {
      const filesArray = Array.from(files).slice(0, availableRefImgsCount)

      // Check if total file size exceeds available storage
      const totalFilesSize = calculateTotalFilesSize(filesArray)

      const { canUpload } = await checkStorageBeforeUpload(
        totalFilesSize,
        dispatch,
        store.getState
      )

      if (!canUpload) {
        setAlertPopupsData({
          fullStoragePopup: {
            isOpen: true
          }
        })
        return
      }

      setUploadingState()
      try {
        const mediaItems = convertImgFilesToMediaItem(filesArray)

        await dispatch(aiImageThunks.uploadRefImages(mediaItems))
      } catch (error) {
        throw new Error('Failed to upload reference images', { cause: error })
      } finally {
        resetUploadingState()
      }
    },
    [
      availableRefImgsCount,
      setUploadingState,
      resetUploadingState,
      setAlertPopupsData
    ]
  )

  const validateMyFiles = useMyFilesFormatValidator('image')

  const handleMediaLibFiles = useCallback(
    async (files: IMyFilesMediaItem[]) => {
      const validFiles = validateMyFiles(files)
      if (validFiles.length === 0) {
        showWarning?.(true)
        return
      }

      setUploadingState()
      try {
        const refImages = convertMediaFilesToReferenceImages(validFiles)

        dispatch(aiImageThunks.addReferenceImage(refImages))
      } catch (error) {
        throw new Error('Failed to add media library files', { cause: error })
      } finally {
        resetUploadingState()
      }
    },
    [setUploadingState, resetUploadingState, validateMyFiles, showWarning]
  )

  return {
    isFilesUploading: isRefImagesUploading,
    handleFilesUpload,
    handleMediaLibFiles
  }
}
